import { useNavigate } from 'react-router';
import { formatMinorAmount, getMinorUnitsSync } from '@sharecost/shared';
import { format, parseISO } from 'date-fns';
import type { TopExpense } from '@/api/statistics';
import { useTranslation } from 'react-i18next';

interface TopExpensesTableProps {
  data: TopExpense[];
  currency: string;
}

export function TopExpensesTable({ data, currency }: TopExpensesTableProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const minorUnits = getMinorUnitsSync(currency);

  if (data.length === 0) {
    return null;
  }

  return (
    <div className="rounded-xl bg-surface-primary shadow-sm ring-1 ring-border-primary overflow-hidden">
      <div className="px-5 py-4 border-b border-border-tertiary">
        <h3 className="text-sm font-semibold text-text-primary">{t('web:topExpensesTable.topExpenses')}</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border-tertiary text-left text-xs font-medium text-text-tertiary">
              <th className="px-5 py-3">{t('expenses:form.description.label')}</th>
              <th className="px-5 py-3">{t('expenses:detail.group')}</th>
              <th className="px-5 py-3">{t('expenses:detail.paidBy')}</th>
              <th className="px-5 py-3">{t('expenses:form.category.label')}</th>
              <th className="px-5 py-3 text-right">{t('settlements:section.amount')}</th>
              <th className="px-5 py-3 text-right">{t('settlements:view.date')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border-tertiary">
            {data.map(expense => (
              <tr
                key={expense.id}
                onClick={() => navigate(`/expenses/${expense.id}`)}
                className="cursor-pointer hover:bg-surface-hover transition-colors"
              >
                <td className="px-5 py-3 font-medium text-text-primary max-w-[200px] truncate">
                  {expense.description}
                </td>
                <td className="px-5 py-3">
                  <span className="flex items-center gap-1.5">
                    <span
                      className="inline-block h-2 w-2 shrink-0 rounded-full"
                      style={{ backgroundColor: expense.groupColor }}
                    />
                    <span className="truncate text-text-secondary">{expense.groupName}</span>
                  </span>
                </td>
                <td className="px-5 py-3 text-text-secondary">{expense.paidByName}</td>
                <td className="px-5 py-3 text-text-tertiary">
                  {expense.categoryName ?? '—'}
                </td>
                <td className="px-5 py-3 text-right font-medium text-text-primary">
                  {formatMinorAmount(expense.amount, currency, minorUnits)}
                </td>
                <td className="px-5 py-3 text-right text-text-tertiary">
                  {format(parseISO(expense.date), 'MMM d, yyyy')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
